import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  AppBar,
  Toolbar,
  Slide,
  Avatar,
  Card,
  CardContent,
  useTheme,
  Tooltip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Add,
  Print,
  Payment,
  Visibility,
  Close,
  AttachMoney,
  CreditCard,
  AccountBalance,
  QrCode,
  AccountBalanceWallet,
  ReceiptLong,
  CheckCircle,
  Cancel,
  CalendarMonth,
  Save,
  Block,
} from "@mui/icons-material";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  addNewReceipt,
  deleteReceipt,
  listAllReceipts,
  resendBookingEmails,
} from "../../../store/actions";
import ReceiptDocument from "./ReceiptDocument";
import ConfirmModal from "../../../components/ConfirmModal";
import { getFinalBookingTotal } from "../../../utils";

const PAYMENT_MODES = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
];

const INITIAL_FORM_STATE = {
  amount: "",
  date: moment().format("YYYY-MM-DD"),
  mode: "cash",
  transaction_ref: "",
  collected_by: "",
  bank_name: "",
  instrument_date: "",
  remarks: "",
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// --- Helper Components ---

const PaymentIcon = ({ mode, colors }) => {
  const iconMap = {
    cash: {
      icon: <AttachMoney sx={{ fontSize: 18 }} />,
      color: colors.greenAccent[600],
    },
    card: {
      icon: <CreditCard sx={{ fontSize: 18 }} />,
      color: colors.blueAccent[600],
    },
    upi: {
      icon: <QrCode sx={{ fontSize: 18 }} />,
      color: colors.greenAccent[500],
    },
    bank_transfer: {
      icon: <AccountBalance sx={{ fontSize: 18 }} />,
      color: colors.grey[500],
    },
    cheque: {
      icon: <ReceiptLong sx={{ fontSize: 18 }} />,
      color: colors.grey[700],
    },
    default: {
      icon: <AccountBalanceWallet sx={{ fontSize: 18 }} />,
      color: colors.primary[300],
    },
  };

  const { icon, color } = iconMap[mode] || iconMap.default;

  return <Avatar sx={{ bgcolor: color, width: 30, height: 30 }}>{icon}</Avatar>;
};

const StatCard = ({ title, value, icon, color, subColor }) => (
  <Card
    sx={{ bgcolor: color, color: "#fff", minWidth: 200, flex: 1, boxShadow: 3 }}
  >
    <CardContent
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        pb: "16px !important",
      }}
    >
      <Box>
        <Typography
          variant="subtitle2"
          sx={{ opacity: 0.8, textTransform: "uppercase", fontSize: "0.75rem" }}
        >
          {title}
        </Typography>
        <Typography variant="h4" fontWeight="bold">
          ₹ {value ? value.toLocaleString() : "0"}
        </Typography>
      </Box>
      <Box
        sx={{ p: 1, borderRadius: "50%", bgcolor: subColor, display: "flex" }}
      >
        {icon}
      </Box>
    </CardContent>
  </Card>
);

const ReceiptActionButtons = ({
  row,
  handlePreview,
  bookingDetails,
  hotelProfile,
}) => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  if (!row.is_active) return null;
  const onVoid = () => {
    console.log("Void action deleteReceipt", row);
    dispatch(
      deleteReceipt({ _id: row._id, booking_id: bookingDetails.booking_id })
    );
  };

  return (
    <Stack direction="row" justifyContent="center" spacing={0}>
      <Tooltip title="Preview Receipt">
        <IconButton size="small" onClick={() => handlePreview(row)}>
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>
      <PDFDownloadLink
        document={
          <ReceiptDocument
            receipt={row}
            bookingDetails={bookingDetails}
            hotelProfile={hotelProfile}
          />
        }
        fileName={`${row.receipt_no}.pdf`}
      >
        {({ loading }) => (
          <Tooltip title="Download PDF">
            <IconButton size="small" color="secondary" disabled={loading}>
              <Print fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </PDFDownloadLink>
      <Tooltip title="Void Receipt">
        <IconButton
          size="small"
          color="error" // Red color to indicate destructive action
          onClick={() => setOpen(true)}
        >
          <Block fontSize="small" />
        </IconButton>
      </Tooltip>

      <ConfirmModal
        open={open}
        setModal={setOpen}
        text={`Are you sure you want to void receipt #${row.receipt_no}?`}
        action={onVoid}
      />
    </Stack>
  );
};

// --- Sub-Modals ---

const AddReceiptModal = ({
  open,
  onClose,
  formData,
  handleChange,
  onVerify,
  colors,
}) => {
  const isCash = formData.mode === "cash";
  const showBankFields = ["cheque", "bank_transfer"].includes(formData.mode);
  const showDateFields = ["cheque"].includes(formData.mode);

  const getRefLabel = (mode) => {
    const labels = {
      cheque: "Cheque Number",
      bank_transfer: "UTR / IMPS Reference",
      card: "Auth Code / Last 4 Digits",
      upi: "UPI Reference ID",
    };
    return labels[mode] || "Transaction Reference";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: colors.blueAccent[700], color: "#fff" }}>
        New Advance Payment
      </DialogTitle>
      <DialogContent sx={{ mt: 2, bgcolor: colors.primary[500], pt: 3 }}>
        <Box component="form" sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Payment Date"
                type="date"
                name="date"
                fullWidth
                value={formData.date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Payment Mode"
                select
                name="mode"
                fullWidth
                value={formData.mode}
                onChange={handleChange}
              >
                {PAYMENT_MODES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Amount Collected"
                type="number"
                name="amount"
                fullWidth
                variant="filled"
                color="secondary"
                value={formData.amount}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <Typography
                      sx={{
                        mr: 1,
                        color: colors.greenAccent[500],
                        fontWeight: "bold",
                      }}
                    >
                      ₹
                    </Typography>
                  ),
                  sx: { fontSize: 20, fontWeight: "bold" },
                }}
              />
            </Grid>

            {isCash ? (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                  Cash payments must track the staff member.
                </Alert>
                <TextField
                  label="Collected By (Staff Name)"
                  name="collected_by"
                  fullWidth
                  required
                  value={formData.collected_by}
                  onChange={handleChange}
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  label={getRefLabel(formData.mode)}
                  name="transaction_ref"
                  fullWidth
                  required
                  value={formData.transaction_ref}
                  onChange={handleChange}
                />
              </Grid>
            )}

            {showBankFields && (
              <Grid item xs={12} sm={showDateFields ? 6 : 12}>
                <TextField
                  label="Bank Name (Sender)"
                  name="bank_name"
                  fullWidth
                  value={formData.bank_name}
                  onChange={handleChange}
                  placeholder="e.g. HDFC, SBI"
                />
              </Grid>
            )}

            {showDateFields && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Cheque Date"
                  type="date"
                  name="instrument_date"
                  fullWidth
                  value={formData.instrument_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="Remarks / Note"
                name="remarks"
                fullWidth
                multiline
                rows={2}
                value={formData.remarks}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, bgcolor: colors.primary[500] }}>
        <Button onClick={onClose} sx={{ color: colors.grey[300] }}>
          Cancel
        </Button>
        <Button
          onClick={onVerify}
          variant="contained"
          color="secondary"
          startIcon={<Payment />}
          sx={{ px: 4, fontWeight: "bold" }}
        >
          Proceed
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ConfirmReceiptModal = ({
  open,
  onClose,
  amount,
  onSubmit,
  mailState,
  setMailState,
  colors,
  isSubmitting,
}) => (
  <Dialog
    open={open}
    onClose={isSubmitting ? undefined : onClose}
    maxWidth="xs"
    fullWidth
  >
    <DialogTitle sx={{ bgcolor: colors.greenAccent[700], color: "#fff" }}>
      Confirm Payment
    </DialogTitle>

    <DialogContent sx={{ mt: 2, bgcolor: colors.primary[500], pt: 3 }}>
      <Typography variant="body1" sx={{ mb: 2 }}>
        You are about to record a payment of{" "}
        <strong style={{ color: colors.greenAccent[400] }}>
          ₹ {Number(amount).toLocaleString()}
        </strong>
        .
      </Typography>

      <Typography variant="body2" sx={{ mb: 2, color: colors.grey[300] }}>
        Please select notification preferences:
      </Typography>

      <Stack spacing={1}>
        <Box
          sx={{
            p: 1,
            border: `1px solid ${colors.grey[700]}`,
            borderRadius: 1,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                disabled={isSubmitting}
                checked={mailState.receipt}
                onChange={(e) =>
                  setMailState((prev) => ({
                    ...prev,
                    receipt: e.target.checked,
                  }))
                }
              />
            }
            label="Send Receipt Email to Guest"
          />
        </Box>

        <Box
          sx={{
            p: 1,
            border: `1px solid ${colors.grey[700]}`,
            borderRadius: 1,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                disabled={isSubmitting}
                checked={mailState.update}
                onChange={(e) =>
                  setMailState((prev) => ({
                    ...prev,
                    update: e.target.checked,
                  }))
                }
              />
            }
            label="Send Updated Booking Summary"
          />
        </Box>
      </Stack>
    </DialogContent>

    <DialogActions sx={{ p: 2 }}>
      <Button onClick={onClose} disabled={isSubmitting}>
        Back
      </Button>

      <Button
        onClick={onSubmit}
        disabled={isSubmitting}
        variant="contained"
        color="success"
        startIcon={isSubmitting ? <CircularProgress size={18} /> : <Save />}
        sx={{ px: 3, fontWeight: "bold" }}
      >
        {isSubmitting ? "Saving..." : "Confirm & Save"}
      </Button>
    </DialogActions>
  </Dialog>
);

const PreviewReceiptModal = ({
  open,
  onClose,
  receipt,
  bookingDetails,
  hotelProfile,
  colors,
}) => (
  <Dialog
    fullScreen
    open={open}
    onClose={onClose}
    TransitionComponent={Transition}
  >
    <AppBar sx={{ position: "relative", bgcolor: colors.blueAccent[700] }}>
      <Toolbar>
        <IconButton edge="start" color="inherit" onClick={onClose}>
          <Close />
        </IconButton>
        <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
          Receipt Preview
        </Typography>
        <Button autoFocus color="inherit" onClick={onClose}>
          Close
        </Button>
      </Toolbar>
    </AppBar>
    <DialogContent sx={{ p: 0, height: "100%", bgcolor: "#525659" }}>
      {receipt && (
        <PDFViewer width="100%" height="100%" style={{ border: "none" }}>
          <ReceiptDocument
            receipt={receipt}
            bookingDetails={bookingDetails}
            hotelProfile={hotelProfile}
          />
        </PDFViewer>
      )}
    </DialogContent>
  </Dialog>
);

// --- Main Component ---

const AdvancePaymentTab = ({ bookingDetails, colors }) => {
  const dispatch = useDispatch();

  const receiptsList = useSelector((state) => state.booking.receipts);
  const loading = useSelector((state) => state.booking.loading);
  const hotelProfile = useSelector((state) => state.registration?.hotelProfile);
  const receipts = useMemo(() => {
    if (!Array.isArray(receiptsList)) return [];

    return [...receiptsList].sort((a, b) => {
      return Number(b.is_active) - Number(a.is_active);
    });
  }, [receiptsList]);

  const [modals, setModals] = useState({
    add: false,
    confirm: false,
    preview: false,
  });
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [mailState, setMailState] = useState({ receipt: true, update: false });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (bookingDetails?.booking_id) {
      dispatch(listAllReceipts({ booking_id: bookingDetails.booking_id }));
    }
  }, [dispatch, bookingDetails?.booking_id]);

  // Handlers
  const toggleModal = (modalName, isOpen) =>
    setModals((prev) => ({ ...prev, [modalName]: isOpen }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "mode") {
      setFormData({
        ...INITIAL_FORM_STATE,
        amount: formData.amount,
        date: formData.date,
        mode: value,
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleVerifyPayment = () => {
    if (!formData.amount || formData.amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (formData.mode === "cash" && !formData.collected_by) {
      toast.error("For cash payments, 'Collected By' is required.");
      return;
    }

    if (formData.mode !== "cash" && !formData.transaction_ref) {
      toast.error("Please enter the transaction reference.");
      return;
    }

    toggleModal("confirm", true);
  };

  const handleFinalSubmit = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const payload = {
        booking_id: bookingDetails?.booking_id,
        hotel_id: bookingDetails?.hotel_id,
        amount: Number(formData.amount),
        date: formData.date ? moment(formData.date).valueOf() : null,
        mode: formData.mode,
        transaction_ref:
          formData.mode === "cash" ? null : formData.transaction_ref,
        bank_name: formData.bank_name,
        instrument_date: formData.instrument_date
          ? moment(formData.instrument_date).valueOf()
          : null,
        collected_by: formData.mode === "cash" ? formData.collected_by : null,
        remarks: formData.remarks,
        send_advance_receipt_mail: mailState.receipt,
      };

      const success = await dispatch(addNewReceipt(payload));

      if (success) {
        if (mailState.update) {
          await dispatch(
            resendBookingEmails({
              bookingId: bookingDetails?.booking_id,
              isForUpdate: true,
            })
          );
          toast.success("Booking update email sent!");
        }

        toggleModal("confirm", false);
        toggleModal("add", false);
        setFormData(INITIAL_FORM_STATE);
        setMailState({ receipt: true, update: false });
        setIsSubmitting(false);
      } else {
        toast.error("Failed to add receipt. Please try again.");
        setIsSubmitting(false); // ✅ allow retry
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong.");
      setIsSubmitting(false);
    }
  };

  const handlePreview = (receipt) => {
    setSelectedReceipt(receipt);
    toggleModal("preview", true);
  };

  // Calculations
  const totalPaid = receipts
    .filter((r) => r.is_active)
    .reduce((sum, r) => sum + (r.amount || 0), 0);
  const bookingTotal = getFinalBookingTotal(bookingDetails);
  const totalDue = (bookingTotal || 0) - totalPaid;


  return (
    <Box>
      {/* Statistics */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={4}>
        <StatCard
          title="Total Booking Value"
          value={bookingTotal || 0}
          icon={<ReceiptLong />}
          color={colors.primary[400]}
          subColor={colors.primary[500]}
        />
        <StatCard
          title="Total Collected"
          value={totalPaid}
          icon={<CheckCircle />}
          color={colors.greenAccent[600]}
          subColor={colors.greenAccent[700]}
        />
        <StatCard
          title="Balance Pending"
          value={totalDue}
          icon={<Cancel />}
          color={colors.redAccent[600]}
          subColor={colors.redAccent[700]}
        />
      </Stack>

      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        mt={4}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
            Payment History
          </Typography>
          <Typography variant="body2" color={colors.grey[400]}>
            Manage advance receipts and transactions
          </Typography>
        </Box>
        <Button
          variant="contained"
          sx={{
            bgcolor: colors.blueAccent[600],
            "&:hover": { bgcolor: colors.blueAccent[700] },
            fontWeight: "bold",
            px: 3,
          }}
          startIcon={<Add />}
          onClick={() => toggleModal("add", true)}
          disabled={bookingDetails?.status === "cancelled"}
        >
          New Payment
        </Button>
      </Stack>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{ bgcolor: colors.primary[400], borderRadius: 2, boxShadow: 4 }}
      >
        {loading ? (
          <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <Table>
            <TableHead sx={{ bgcolor: colors.blueAccent[800] }}>
              <TableRow>
                {[
                  "Receipt Details",
                  "Payment Mode & Ref",
                  "Details",
                  "Amount",
                  "Status",
                  "Actions",
                ].map((head, i) => (
                  <TableCell
                    key={i}
                    align={i > 2 ? "center" : "left"}
                    sx={{ color: "#fff", fontWeight: "bold" }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {receipts.length > 0 ? (
                receipts.map((row) => (
                  <TableRow
                    key={row._id}
                    sx={{
                      opacity: row.is_active ? 1 : 0.6,
                      "&:hover": { bgcolor: colors.primary[500] },
                      borderBottom: `1px solid ${colors.primary[500]}`,
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={colors.greenAccent[400]}
                      >
                        {row.receipt_no}
                      </Typography>
                      <Typography variant="caption" color={colors.grey[400]}>
                        {moment(Number(row.date)).format("DD MMM, YYYY")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <PaymentIcon mode={row.mode} colors={colors} />
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ textTransform: "capitalize" }}
                          >
                            {row.mode}
                          </Typography>
                          {row.transaction_ref && (
                            <Typography
                              variant="caption"
                              color={colors.grey[300]}
                              sx={{ display: "block" }}
                            >
                              Ref: {row.transaction_ref}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        {row.mode === "cash" ? (
                          <Typography
                            variant="caption"
                            color={colors.grey[300]}
                          >
                            Collected by: {row.collected_by}
                          </Typography>
                        ) : (
                          <>
                            {row.bank_name && (
                              <Typography
                                variant="caption"
                                color={colors.grey[200]}
                                sx={{ display: "flex", gap: 0.5 }}
                              >
                                <AccountBalance sx={{ fontSize: 14 }} />{" "}
                                {row.bank_name}
                              </Typography>
                            )}
                            {row.instrument_date && (
                              <Typography
                                variant="caption"
                                color={colors.grey[400]}
                                sx={{ display: "flex", gap: 0.5 }}
                              >
                                <CalendarMonth sx={{ fontSize: 14 }} />{" "}
                                {moment(Number(row.instrument_date)).format(
                                  "DD MMM"
                                )}
                              </Typography>
                            )}
                          </>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color={
                          row.is_active
                            ? colors.greenAccent[500]
                            : colors.grey[500]
                        }
                      >
                        ₹ {row.amount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.is_active ? "Success" : "Voided"}
                        size="small"
                        variant="filled"
                        sx={{
                          bgcolor: row.is_active
                            ? "rgba(76, 175, 80, 0.2)"
                            : "rgba(244, 67, 54, 0.2)",
                          color: row.is_active ? "#4caf50" : "#f44336",
                          fontWeight: "bold",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <ReceiptActionButtons
                        row={row}
                        handlePreview={handlePreview}
                        bookingDetails={bookingDetails}
                        hotelProfile={hotelProfile}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <ReceiptLong
                      sx={{ fontSize: 40, color: colors.grey[500], mb: 1 }}
                    />
                    <Typography color="textSecondary">
                      No advance payments recorded.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Modals */}
      <AddReceiptModal
        open={modals.add}
        onClose={() => toggleModal("add", false)}
        formData={formData}
        handleChange={handleChange}
        onVerify={handleVerifyPayment}
        colors={colors}
      />
      <ConfirmReceiptModal
        open={modals.confirm}
        onClose={() => toggleModal("confirm", false)}
        amount={formData.amount}
        onSubmit={handleFinalSubmit}
        mailState={mailState}
        setMailState={setMailState}
        colors={colors}
        isSubmitting={isSubmitting}
      />
      <PreviewReceiptModal
        open={modals.preview}
        onClose={() => toggleModal("preview", false)}
        receipt={selectedReceipt}
        bookingDetails={bookingDetails}
        hotelProfile={hotelProfile}
        colors={colors}
      />
    </Box>
  );
};

export default AdvancePaymentTab;
